// src/pages/Blogs.jsx
// The dispatches archive: full list of all blog posts, fetched from the backend.
//
// Data flow:
// 1. On mount, useEffect calls getAllBlogs() (which hits GET /api/blogs).
// 2. The backend returns { success, data, message }; the array of posts is at .data.
// 3. We store the array in state and render each post as an editorial row.
// 4. Loading / error / empty states render their own UI variants.
//
// Each post links to /blogs/:id, which is the SingleBlog page.
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getAllBlogs } from "../api/blogs";

// Format an ISO timestamp into "Apr 12, 2026" style.
function formatDate(iso) {
  if (!iso) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(iso));
}

// Truncate long body text for preview display.
function excerpt(text, max = 160) {
  if (!text) return "";
  if (text.length <= max) return text;
  return text.slice(0, max).trimEnd() + "…";
}

export default function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Define an async function inside useEffect (useEffect itself can't be async).
    async function fetchBlogs() {
      try {
        const response = await getAllBlogs();
        // Backend returns { success, data, message }. The array is at .data.
        setBlogs(response.data || []);
      } catch (err) {
        console.error("Failed to fetch blogs:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    fetchBlogs();
  }, []);

  return (
    <section className="max-w-wide mx-auto px-6 py-20 md:py-28">
      {/* Section header */}
      <div className="mb-12">
        <p className="label-mono">Dispatches</p>
        <h1 className="font-display text-h2 leading-tight font-medium mt-3">
          The archive
        </h1>
        <p className="text-body text-ink-70 leading-relaxed mt-6 max-w-2xl">
          Field notes, essays, and dispatches from the practice. Filed in
          reverse chronological order.
        </p>
      </div>

      {/* Loading state */}
      {loading && (
        <p className="label-mono text-ink-faint">Loading dispatches…</p>
      )}

      {/* Error state (graceful: console has the details, UI stays calm) */}
      {!loading && error && (
        <p className="label-mono text-ink-faint">
          The archive is temporarily unavailable. Please check back shortly.
        </p>
      )}

      {/* Empty state */}
      {!loading && !error && blogs.length === 0 && (
        <p className="label-mono text-ink-faint">No dispatches yet.</p>
      )}

      {/* Populated list */}
      {!loading && !error && blogs.length > 0 && (
        <div className="border-t border-ink-20">
          {blogs.map((blog) => (
            <article key={blog.id} className="border-b border-ink-20 py-8">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-baseline">
                <p className="label-mono md:col-span-2">
                  {formatDate(blog.created_at)}
                </p>
                <div className="md:col-span-8">
                  <h2 className="font-display text-h3 font-medium leading-tight mb-2">
                    <Link
                      to={`/blogs/${blog.id}`}
                      className="hover:text-oxblood transition-colors"
                    >
                      {blog.title}
                    </Link>
                  </h2>
                  <p className="text-body text-ink-70 leading-relaxed">
                    {excerpt(blog.content)}
                  </p>
                </div>
                <div className="md:col-span-2 md:text-right">
                  <p className="label-mono">{blog.user?.name || "Unknown"}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
